import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { TransactionType } from '@/models/smaregi/transaction'
import { AdjustmentSalesSelectList } from '@/components/molecules/customer/sales-manager/adjustment-sales-select-list'
import { ButtonType } from '@/components/atoms/button'
import { TerminalCalculateInProgressModal } from './terminal-calculate-in-progress-modal'
import { fetchPaymentSetting } from '@/apis/aipass'
import { PaymentTerminalSettingType } from '@/models/payment-setting'

type ModalProps = {
  reservationId?: string
  checkinId?: string
  transaction?: TransactionType
  onClose: (isUpdated: boolean) => void
  filterGuestRoomAssignId?: string[]
}

export const TerminalCalculateModal: React.FC<ModalProps> = props => {
  const { t } = useTranslation()
  const [isDisabledSend, setIsDisabledSend] = useState<boolean>(true)
  const [selectPaymentMethod, setSelectPaymentMethod] = useState<'credit' | 'unionPay' | 'qr'>()
  const [selectedSaleIds, setSelectedSaleIds] = useState<string[]>([])
  const [sumSelectSalePrice, setSumSelectSalePrice] = useState<number>(0)
  const [terminalSettings, setTerminalSettings] = useState<PaymentTerminalSettingType>()

  const selectableMethodNum: number = useMemo(() => (terminalSettings?.isEnabledQr ? 3 : 2), [terminalSettings])

  useEffect(() => {
    setIsDisabledSend(!selectedSaleIds.length)
  }, [selectedSaleIds])

  useEffect(() => {
    fetchPaymentSetting().then(({ terminals }) => setTerminalSettings(terminals))
  }, [])

  return (
    <>
      <div css={modalOverlayStyle} />
      <div css={modalScrollContainerStyle}>
        <div css={modalContentStyle}>
          <div css={modalCloseStyle}>
            <img src={require('@/static/images/delete_orange.svg')} width={30} onClick={() => props.onClose(false)} />
          </div>

          <div css={contentWrapperContainerStyle}>
            <div css={contentWrapperStyle} style={{ paddingBottom: 24 }}>
              <div>
                <div className="sub-title">{t('Settlement subjects')}</div>
                <AdjustmentSalesSelectList
                  {...props}
                  onChange={(selectedSaleIds: string[], amount: number) => {
                    setSelectedSaleIds(selectedSaleIds)
                    setSumSelectSalePrice(amount)
                  }}
                />
              </div>
            </div>

            <div css={contentWrapperStyle}>
              <div>
                <div className="sub-title">{t('Method of payment')}</div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <Button
                    width={`${100 / selectableMethodNum}%}}`}
                    height={38}
                    buttonType={isDisabledSend ? ButtonType.PrimaryDisabled : ButtonType.Primary}
                    isDisabled={isDisabledSend}
                    onClick={() => setSelectPaymentMethod('credit')}
                  >
                    {t('Credit Card')}
                  </Button>
                  <Button
                    width={`${100 / selectableMethodNum}%}}`}
                    height={38}
                    buttonType={isDisabledSend ? ButtonType.PrimaryDisabled : ButtonType.Primary}
                    isDisabled={isDisabledSend}
                    onClick={() => setSelectPaymentMethod('unionPay')}
                  >
                    {t('UnionPay')}
                  </Button>
                  {terminalSettings?.isEnabledQr && (
                    <Button
                      width={`${100 / selectableMethodNum}%}}`}
                      height={38}
                      buttonType={isDisabledSend ? ButtonType.PrimaryDisabled : ButtonType.Primary}
                      isDisabled={isDisabledSend}
                      onClick={() => setSelectPaymentMethod('qr')}
                    >
                      {t('QR Payment')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectPaymentMethod && (
        <TerminalCalculateInProgressModal
          onCancel={() => setSelectPaymentMethod(undefined)}
          onSuccess={() => {
            setSelectPaymentMethod(undefined)
            props.onClose(true)
          }}
          type={selectPaymentMethod}
          amount={sumSelectSalePrice}
          salesIds={selectedSaleIds}
        />
      )}
    </>
  )
}

const modalOverlayStyle = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  zIndex: 100,
})

const modalScrollContainerStyle = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  zIndex: 100,
})

const modalContentStyle = css({
  width: 900,
  background: '#fff',
  borderRadius: 5,
  margin: '10vh auto 24px',
})

const contentWrapperStyle = css({
  width: '100%',
  padding: 32,
  paddingTop: 0,
  background: '#f2f2f2',
  display: 'flex',
  flexFlow: 'column',
  div: { color: '#676767' },
  '>div': {
    background: 'white',
    padding: 32,
    borderRadius: 5,
  },
  '.sub-title': {
    paddingBottom: 12,
    fontWeight: 'bold',
    fontSize: 18,
  },
})

const modalCloseStyle = css({
  width: '100%',
  textAlign: 'end',
  background: '#f2f2f2',
  height: 32,
  padding: '2px 2px 0 0',
  '>img': { cursor: 'pointer' },
})

const contentWrapperContainerStyle = css({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
})
